import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  Bot,
  Copy,
  Check,
  Globe,
  ShieldCheck,
  AlertTriangle,
  Loader2,
  Rocket,
  Plus,
  X,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useApi from '../../api';
import AgentTabHeader from './AgentTabHeader';
import { AgentGatewayTabProps } from './types';

const looksLikeDomain = (value: string) => /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value.trim());

interface GatewayEndpoints {
  intro: string;
  ask: string;
  messages: string;
  manifest: string;
  mcp: string;
}

interface GatewayKit {
  agent_id: string;
  agent_name: string;
  enabled: boolean;
  endpoints: GatewayEndpoints;
  link_rel: string;
  candidate_domains: string[];
}

interface StatusCheck {
  id: string;
  label: string;
  status: 'found' | 'missing' | 'error';
  url?: string;
  detail?: string;
}

interface StatusResult {
  domain: string;
  discoverable: boolean;
  checks: StatusCheck[];
}

interface SiteStatus {
  loading: boolean;
  result?: StatusResult;
  error?: string;
}

// ---- Artifact + snippet generators (single source of truth is the backend
// `endpoints`; these just format them into copy-paste files). ----

const llmsTxt = (name: string, e: GatewayEndpoints) =>
  `# ${name}

> AI Agent Gateway — AI agents can talk to our assistant directly instead of parsing this website.

## Agent Gateway
- [Start here](${e.intro}): Instructions for AI agents to ask about our product, pricing, integrations, fit, or to book a demo.
- [MCP endpoint](${e.mcp}): Streamable HTTP MCP server (no authentication required).
- [Manifest](${e.manifest}): Machine-readable capability manifest.
`;

const wellKnownJson = (e: GatewayEndpoints) =>
  JSON.stringify(
    {
      kind: 'aimdoc-agent-gateway',
      version: 1,
      gateway_url: e.intro,
      mcp_url: e.mcp,
      manifest_url: e.manifest,
    },
    null,
    2,
  );

const linkTag = (e: GatewayEndpoints, rel: string) =>
  `<link rel="${rel}" href="${e.manifest}">`;

const linkHeader = (e: GatewayEndpoints, rel: string) =>
  `Link: <${e.manifest}>; rel="${rel}"`;

// /mcp uses 307 (not 302) so MCP clients that POST to it survive the hop with
// their method and body intact; 302 gets rewritten to GET by most clients.
const redirectSnippets = (e: GatewayEndpoints): { value: string; label: string; code: string }[] => [
  {
    value: 'cloudflare',
    label: 'Cloudflare',
    code: `# Cloudflare Pages — add to a _redirects file at your site root.
# (For a non-Pages zone, create the same rules under Rules → Redirect
#  Rules; keep 307 for /mcp so POSTs are preserved.)
/mcp  ${e.mcp}  307
/.well-known/aimdoc-agent.json  ${e.manifest}  302
/llms.txt  ${e.intro}  302`,
  },
  {
    value: 'vercel',
    label: 'Vercel',
    code: `// vercel.json — permanent: false emits a 307, which preserves POSTs to /mcp.
{
  "redirects": [
    { "source": "/mcp", "destination": "${e.mcp}", "permanent": false },
    { "source": "/.well-known/aimdoc-agent.json", "destination": "${e.manifest}", "permanent": false },
    { "source": "/llms.txt", "destination": "${e.intro}", "permanent": false }
  ]
}`,
  },
  {
    value: 'netlify',
    label: 'Netlify',
    code: `# Netlify — add to a _redirects file at your publish root.
/mcp  ${e.mcp}  307
/.well-known/aimdoc-agent.json  ${e.manifest}  302
/llms.txt  ${e.intro}  302`,
  },
  {
    value: 'nginx',
    label: 'nginx',
    code: `# nginx — inside your server { } block.
location = /mcp { return 307 ${e.mcp}; }
location = /.well-known/aimdoc-agent.json { return 302 ${e.manifest}; }
location = /llms.txt { return 302 ${e.intro}; }`,
  },
  {
    value: 'nextjs',
    label: 'Next.js',
    code: `// next.config.js — permanent: false emits a 307, which preserves POSTs to /mcp.
module.exports = {
  async redirects() {
    return [
      { source: '/mcp', destination: '${e.mcp}', permanent: false },
      { source: '/.well-known/aimdoc-agent.json', destination: '${e.manifest}', permanent: false },
      { source: '/llms.txt', destination: '${e.intro}', permanent: false },
    ];
  },
};`,
  },
];

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy', toastConfig);
    }
  }, [text]);
  return (
    <Button variant="outline" size="sm" onClick={onCopy} className="h-7 shrink-0 gap-1.5 text-xs">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label || 'Copy'}
    </Button>
  );
}

function CodeBlock({ title, code }: { title: string; code: string }) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-lg border bg-muted/40">
      <div className="flex min-w-0 items-center justify-between gap-2 border-b bg-muted/60 px-3 py-1.5">
        <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground">{title}</span>
        <CopyButton text={code} />
      </div>
      <pre className="max-w-full overflow-x-auto p-3 text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function EndpointRow({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium">{label}</p>
        <p className="truncate font-mono text-[11px] text-muted-foreground">{url}</p>
      </div>
      <CopyButton text={url} />
    </div>
  );
}

const STATUS_META: Record<
  StatusCheck['status'],
  { icon: JSX.Element; className: string }
> = {
  found: { icon: <Check className="h-4 w-4" />, className: 'text-emerald-600 dark:text-emerald-400' },
  missing: { icon: <AlertTriangle className="h-4 w-4" />, className: 'text-amber-600 dark:text-amber-400' },
  error: { icon: <AlertTriangle className="h-4 w-4" />, className: 'text-muted-foreground' },
};

function SiteStatusPill({ status, domainLike }: { status?: SiteStatus; domainLike: boolean }) {
  if (!status) {
    return domainLike ? (
      <span className="shrink-0 text-[11px] text-muted-foreground">Not checked</span>
    ) : null;
  }
  if (status.loading) {
    return (
      <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
      </span>
    );
  }
  if (status.error) {
    return (
      <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5" /> Couldn&apos;t reach
      </span>
    );
  }
  if (status.result?.discoverable) {
    return (
      <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
        <ShieldCheck className="h-3.5 w-3.5" /> Discoverable
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
      <AlertTriangle className="h-3.5 w-3.5" /> Not found
    </span>
  );
}

export default function AgentGatewayTab({
  agId,
  setNeedsUpdate,
  agentGatewayEnabled,
  setAgentGatewayEnabled,
  agentGatewayDailyCap,
  setAgentGatewayDailyCap,
  agentGatewaySites,
  setAgentGatewaySites,
}: AgentGatewayTabProps) {
  const { get, post } = useApi();
  const [kit, setKit] = useState<GatewayKit | null>(null);
  const [statuses, setStatuses] = useState<Record<string, SiteStatus>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Domains we've already kicked off a check for, so debounced edits don't
  // re-fire on every keystroke. A manual recheck re-adds + refetches.
  const requestedRef = useRef<Set<string>>(new Set());
  // Latest agId, so an async check that started for a previous agent can bail
  // instead of writing its result into the agent now on screen.
  const agIdRef = useRef(agId);
  useEffect(() => {
    agIdRef.current = agId;
  }, [agId]);

  useEffect(() => {
    if (!agId) return;
    let active = true;
    get(`/agents/${agId}/gateway/kit`)
      .then(({ data }) => {
        if (!active) return;
        setKit(data);
      })
      .catch(() => {
        /* endpoints still render from a fallback below */
      });
    return () => {
      active = false;
    };
  }, [agId, get]);

  // Switching agents without unmounting must not carry over another agent's
  // check results (or its already-requested set, which would suppress a
  // re-check of the same hostname under the new agent).
  useEffect(() => {
    requestedRef.current = new Set();
    setKit(null);
    setStatuses({});
    setExpanded({});
  }, [agId]);

  const endpoints = kit?.endpoints;
  const linkRel = kit?.link_rel || 'aimdoc-agent-gateway';
  const snippets = useMemo(() => (endpoints ? redirectSnippets(endpoints) : []), [endpoints]);
  const sites = agentGatewaySites;

  // Live gateway reads the *published* config, so a freshly toggled-on draft
  // isn't discoverable until publish. Surface that instead of a silent no-op.
  const needsPublish = agentGatewayEnabled && kit != null && !kit.enabled;

  // Until the customer curates their own list, fall back to the agent's trained
  // domains for display + checking. This is display-only — nothing is written to
  // config (so a still-loading saved list is never clobbered); the first edit
  // materializes it into persisted state.
  const effectiveSites = useMemo(() => {
    if (sites.length > 0) return sites;
    const seed = (kit?.candidate_domains || []).slice(0, 2);
    return seed.length > 0 ? seed : [''];
  }, [sites, kit]);

  const checkOne = useCallback(
    async (rawDomain: string) => {
      const domain = rawDomain.trim();
      if (!agId || !domain) return;
      const startedFor = agId;
      setStatuses((prev) => ({ ...prev, [domain]: { loading: true } }));
      try {
        const { data } = await post(`/agents/${agId}/gateway/status-check`, { domain });
        // Bail if the user switched agents while this was in flight — otherwise
        // the previous agent's result would land in the one now on screen.
        if (agIdRef.current !== startedFor) return;
        setStatuses((prev) => ({ ...prev, [domain]: { loading: false, result: data } }));
      } catch (err: any) {
        if (agIdRef.current !== startedFor) return;
        setStatuses((prev) => ({
          ...prev,
          [domain]: { loading: false, error: err?.detail || 'Could not reach this site.' },
        }));
      }
    },
    [agId, post],
  );

  // Auto-check on load and as domains settle (debounced). Only fire for
  // domain-shaped values we haven't already requested.
  const sitesKey = effectiveSites.map((s) => s.trim()).filter(Boolean).join('|');
  useEffect(() => {
    if (!agId) return;
    const t = setTimeout(() => {
      effectiveSites
        .map((s) => s.trim())
        .filter((d) => looksLikeDomain(d))
        .forEach((d) => {
          if (requestedRef.current.has(d)) return;
          requestedRef.current.add(d);
          checkOne(d);
        });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitesKey, agId, checkOne]);

  const recheck = useCallback(
    (rawDomain: string) => {
      const d = rawDomain.trim();
      if (!looksLikeDomain(d)) return;
      requestedRef.current.add(d);
      checkOne(d);
    },
    [checkOne],
  );

  const recheckAll = useCallback(() => {
    effectiveSites.map((s) => s.trim()).filter((d) => looksLikeDomain(d)).forEach((d) => recheck(d));
  }, [effectiveSites, recheck]);

  const updateSite = (index: number, value: string) => {
    const next = effectiveSites.slice();
    next[index] = value;
    setAgentGatewaySites(next);
    setNeedsUpdate(true);
  };

  const addSite = () => {
    setAgentGatewaySites([...effectiveSites, '']);
    setNeedsUpdate(true);
  };

  const removeSite = (index: number) => {
    const next = effectiveSites.slice();
    next.splice(index, 1);
    setAgentGatewaySites(next.length ? next : ['']);
    setNeedsUpdate(true);
  };

  // Enable + cap are plain parent state; without marking the form dirty a
  // gateway-only change wouldn't persist (ConfigureAgent gates save on it).
  const handleEnableChange = (value: boolean) => {
    setAgentGatewayEnabled(value);
    setNeedsUpdate(true);
  };

  const handleCapChange = (value: number) => {
    setAgentGatewayDailyCap(value);
    setNeedsUpdate(true);
  };

  return (
    <div className="min-w-0 max-w-full">
      <AgentTabHeader
        icon={Bot}
        title="Agent Gateway"
        description="Let visiting AI agents (Claude, ChatGPT, research tools) talk to your agent and find you."
        actions={(
          <div className="flex items-center gap-2">
            <Label htmlFor="gateway-toggle" className="cursor-pointer text-xs text-muted-foreground">
              {agentGatewayEnabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="gateway-toggle"
              checked={agentGatewayEnabled}
              onCheckedChange={handleEnableChange}
            />
          </div>
        )}
      />

      <div className="mx-auto min-w-0 w-full max-w-4xl space-y-8 py-6">
        {!agentGatewayEnabled && (
          <div className="rounded-lg border bg-card p-6 text-center">
            <p className="text-sm font-medium">The agent gateway is off</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
              Turn it on to open a public endpoint where AI agents converse with your agent instead of
              parsing your website, and to set up discovery on your sites. Sessions appear in your
              dashboard marked &quot;AI Agent&quot;.
            </p>
          </div>
        )}

        {agentGatewayEnabled && (
          <>
            {needsPublish && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5">
                <Rocket className="mt-0.5 h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                <p className="text-xs leading-5 text-amber-700 dark:text-amber-300">
                  Publish your agent to activate the gateway. The public endpoint and discovery checks
                  use your <span className="font-medium">published</span> configuration.
                </p>
              </div>
            )}

            {/* Your sites — persisted list, auto-checked on load */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold">Your sites</h3>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Add your apex domain and any subdomains (docs, www). We check each one for your
                    discovery markers so you can confirm agents can find you.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={recheckAll}
                  className="h-7 shrink-0 gap-1.5 text-xs"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Recheck
                </Button>
              </div>

              <div className="space-y-2">
                {effectiveSites.map((site, i) => {
                  const d = site.trim();
                  const st = d ? statuses[d] : undefined;
                  const open = !!expanded[d];
                  return (
                    <div key={i} className="rounded-lg border bg-card">
                      <div className="flex items-center gap-2 p-2.5">
                        <div className="relative min-w-0 flex-1">
                          <Globe className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={site}
                            onChange={(e) => updateSite(i, e.target.value)}
                            placeholder="yourwebsite.com"
                            className="pl-9"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') recheck(d);
                            }}
                          />
                        </div>
                        <SiteStatusPill status={st} domainLike={looksLikeDomain(d)} />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeSite(i)}
                          aria-label="Remove site"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {st?.result && (
                        <div className="border-t">
                          <button
                            type="button"
                            onClick={() => setExpanded((p) => ({ ...p, [d]: !open }))}
                            className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] text-muted-foreground hover:text-foreground"
                          >
                            <ChevronRight
                              className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
                            />
                            {open ? 'Hide details' : 'Details'}
                          </button>
                          {open && (
                            <div className="divide-y border-t">
                              {st.result.checks.map((c) => {
                                const meta = STATUS_META[c.status];
                                return (
                                  <div key={c.id} className="flex items-start gap-3 px-3 py-2.5">
                                    <span className={`mt-0.5 ${meta.className}`}>{meta.icon}</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium">{c.label}</p>
                                      {c.detail && (
                                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                                          {c.detail}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button variant="outline" size="sm" onClick={addSite} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add site
              </Button>

              <p className="text-[11px] leading-5 text-muted-foreground">
                The embed script also injects a marker for JS-capable agents — that can&apos;t be
                verified by this raw fetch, so it isn&apos;t listed here.
              </p>
            </div>

            {endpoints && (
              <>
                {/* Endpoints */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold">Endpoints</h3>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Share these with AI agents directly, or add them to your docs. Both drive the
                      same conversation engine.
                    </p>
                  </div>
                  <div className="divide-y rounded-lg border bg-card px-4">
                    <EndpointRow label="Gateway (HTTP, for any agent)" url={endpoints.intro} />
                    <EndpointRow label="MCP server (streamable HTTP, no auth)" url={endpoints.mcp} />
                    <EndpointRow label="Manifest (machine-readable)" url={endpoints.manifest} />
                  </div>
                </div>

                {/* Discoverability Kit */}
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold">Discoverability kit</h3>
                      <Badge variant="secondary" className="text-[10px]">
                        Make agents find you
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Agents look for a pointer on <span className="font-medium">your</span> domain.
                      Add it one of two ways — paste a file, or add a redirect rule so Aimdoc keeps the
                      content current. Do both for maximum coverage.
                    </p>
                  </div>

                  <Tabs defaultValue="paste" className="w-full">
                    <TabsList className="h-9">
                      <TabsTrigger value="paste" className="text-xs">
                        Paste a file
                      </TabsTrigger>
                      <TabsTrigger value="redirect" className="text-xs">
                        Add a redirect rule
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="paste" className="mt-3 space-y-3">
                      <p className="text-xs leading-5 text-muted-foreground">
                        Host these two files at your site root. Works everywhere with zero
                        infrastructure — you maintain the content if your endpoints ever change. To
                        also cover <code className="text-[11px]">/mcp</code> (the path AI assistants
                        fetch most), add the redirect rule from the other tab — it can&apos;t be a
                        static file.
                      </p>
                      <CodeBlock
                        title="llms.txt"
                        code={llmsTxt(kit?.agent_name || 'Your company', endpoints)}
                      />
                      <CodeBlock
                        title="/.well-known/aimdoc-agent.json"
                        code={wellKnownJson(endpoints)}
                      />
                      <div className="rounded-lg border bg-card p-4">
                        <p className="text-xs font-medium">Advanced: raw-fetchable markers</p>
                        <p className="mb-3 mt-1 text-xs leading-5 text-muted-foreground">
                          Add to your homepage <code className="text-[11px]">&lt;head&gt;</code>{' '}
                          server-side (so it survives a non-JS fetch), or as an HTTP response header.
                        </p>
                        <div className="space-y-3">
                          <CodeBlock title="HTML <head>" code={linkTag(endpoints, linkRel)} />
                          <CodeBlock
                            title="HTTP response header"
                            code={linkHeader(endpoints, linkRel)}
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="redirect" className="mt-3 space-y-3">
                      <p className="text-xs leading-5 text-muted-foreground">
                        Point the discovery paths at Aimdoc with one rule. Aimdoc owns the content, so
                        it updates automatically. <code className="text-[11px]">/mcp</code> is the
                        path AI assistants fetch most — skip that line if you already serve something
                        there. Pick your platform:
                      </p>
                      <Tabs defaultValue={snippets[0]?.value} className="min-w-0 w-full">
                        <TabsList className="h-auto min-h-9 flex-wrap">
                          {snippets.map((s) => (
                            <TabsTrigger key={s.value} value={s.value} className="text-xs">
                              {s.label}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        {snippets.map((s) => (
                          <TabsContent key={s.value} value={s.value} className="mt-3">
                            <CodeBlock title={s.label} code={s.code} />
                          </TabsContent>
                        ))}
                      </Tabs>
                    </TabsContent>
                  </Tabs>
                </div>
              </>
            )}

            {/* Settings */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Settings</h3>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Daily cap of</span>
                  <Input
                    type="number"
                    value={agentGatewayDailyCap}
                    onChange={(e) => handleCapChange(Number(e.target.value))}
                    className="w-24 text-center"
                    min="1"
                    max="10000"
                  />
                  <span className="text-xs text-muted-foreground">agent messages per day</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
